import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stack,
  Chip,
} from '@mui/material';

interface CustomThemeDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (theme: {
    scheme_name: string;
    description?: string;
    primary_color: string;
    secondary_color: string;
    accent_color?: string;
  }) => Promise<void>;
  existingTheme?: {
    id: number;
    scheme_name: string;
    description?: string;
    primary_color: string;
    secondary_color: string;
    accent_color?: string;
  };
}

const CustomThemeDialog: React.FC<CustomThemeDialogProps> = ({
  open,
  onClose,
  onSave,
  existingTheme
}) => {
  const [themeName, setThemeName] = useState(existingTheme?.scheme_name || '');
  const [description, setDescription] = useState(existingTheme?.description || '');
  const [primaryColor, setPrimaryColor] = useState(existingTheme?.primary_color || '#2E86AB');
  const [secondaryColor, setSecondaryColor] = useState(existingTheme?.secondary_color || '#A23B72');
  const [accentColor, setAccentColor] = useState(existingTheme?.accent_color || '#74C69D');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!themeName.trim()) {
      alert('Please enter a theme name');
      return;
    }

    setSaving(true);
    try {
      await onSave({
        scheme_name: themeName.trim(),
        description: description.trim() || undefined,
        primary_color: primaryColor.toUpperCase(),
        secondary_color: secondaryColor.toUpperCase(),
        accent_color: accentColor.toUpperCase(),
      });
      handleClose();
    } catch (error: any) {
      alert(error?.response?.data?.detail || 'Failed to save custom theme');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setThemeName('');
      setDescription('');
      setPrimaryColor('#2E86AB');
      setSecondaryColor('#A23B72');
      setAccentColor('#74C69D');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {existingTheme ? 'Edit Custom Theme' : 'Create Custom Theme'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Theme Name */}
          <TextField
            label="Theme Name"
            value={themeName}
            onChange={(e) => setThemeName(e.target.value)}
            fullWidth
            required
            helperText="Give your custom theme a memorable name"
          />

          {/* Description */}
          <TextField
            label="Description (Optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            helperText="Brief description of your theme"
          />

          {/* Color Pickers */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Theme Colors
            </Typography>
            
            <Stack spacing={2}>
              {/* Primary Color */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  style={{
                    width: 50,
                    height: 50,
                    border: '2px solid #ccc',
                    borderRadius: 8,
                    cursor: 'pointer'
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Primary Color
                  </Typography>
                  <TextField
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    size="small"
                    placeholder="#2E86AB"
                    inputProps={{ pattern: '^#[0-9A-Fa-f]{6}$' }}
                  />
                </Box>
              </Box>

              {/* Secondary Color */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <input
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  style={{
                    width: 50,
                    height: 50,
                    border: '2px solid #ccc',
                    borderRadius: 8,
                    cursor: 'pointer'
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Secondary Color
                  </Typography>
                  <TextField
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    size="small"
                    placeholder="#A23B72"
                    inputProps={{ pattern: '^#[0-9A-Fa-f]{6}$' }}
                  />
                </Box>
              </Box>

              {/* Accent Color */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <input
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  style={{
                    width: 50,
                    height: 50,
                    border: '2px solid #ccc',
                    borderRadius: 8,
                    cursor: 'pointer'
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Accent Color (Optional)
                  </Typography>
                  <TextField
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    size="small"
                    placeholder="#74C69D"
                    inputProps={{ pattern: '^#[0-9A-Fa-f]{6}$' }}
                  />
                </Box>
              </Box>
            </Stack>
          </Box>

          {/* Live Preview */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Preview
            </Typography>
            <Box sx={{
              p: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper'
            }}>
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                <Chip 
                  label="Primary" 
                  size="small" 
                  sx={{ bgcolor: primaryColor, color: '#fff' }} 
                />
                <Chip 
                  label="Secondary" 
                  size="small" 
                  sx={{ bgcolor: secondaryColor, color: '#fff' }} 
                />
                <Chip 
                  label="Accent" 
                  size="small" 
                  sx={{ bgcolor: accentColor, color: '#fff' }} 
                />
              </Stack>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Box 
                  sx={{ 
                    width: 60, 
                    height: 60, 
                    bgcolor: primaryColor, 
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider'
                  }} 
                />
                <Box 
                  sx={{ 
                    width: 60, 
                    height: 60, 
                    bgcolor: secondaryColor, 
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider'
                  }} 
                />
                <Box 
                  sx={{ 
                    width: 60, 
                    height: 60, 
                    bgcolor: accentColor, 
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider'
                  }} 
                />
              </Box>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={saving || !themeName.trim()}
        >
          {saving ? 'Saving...' : existingTheme ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomThemeDialog;

